/**
 * One source of truth for a real foreground return.
 *
 * Browsers normally emit document visibility and window focus as a pair. Raw
 * listeners treat that one user action as two refresh requests. This state
 * machine instead emits once for each away -> foreground epoch. The public DOM
 * event intentionally contains only timing/reason metadata so browser proof and
 * future telemetry cannot leak a route, client, draft, or user identity.
 */

export const APP_RESUME_EVENT = "lnpro:app-resume";
export const APP_AWAY_EVENT = "lnpro:app-away";
const COALESCE_MS = 200;

export type AppResumeReason = "focus" | "visible";

export interface AppResumeSignal {
  id: number;
  at: number;
  awayMs: number;
  reasons: AppResumeReason[];
}

type ResumeListener = (signal: AppResumeSignal) => void;

let started = false;
let away = false;
let awayAt: number | null = null;
let sequence = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
let pendingReasons = new Set<AppResumeReason>();
const listeners = new Set<ResumeListener>();

const notify = (signal: AppResumeSignal) => {
  listeners.forEach((listener) => {
    try {
      listener(signal);
    } catch {
      // A refresh observer cannot starve the other domains.
    }
  });
  window.dispatchEvent(
    new CustomEvent<AppResumeSignal>(APP_RESUME_EVENT, { detail: signal })
  );
};

const markAway = () => {
  if (!away) awayAt = Date.now();
  away = true;
  if (timer) clearTimeout(timer);
  timer = null;
  pendingReasons.clear();
  window.dispatchEvent(new Event(APP_AWAY_EVENT));
};

const requestResume = (reason: AppResumeReason) => {
  if (!away || document.visibilityState !== "visible") return;
  pendingReasons.add(reason);
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    if (!away || document.visibilityState !== "visible") {
      pendingReasons.clear();
      return;
    }
    const now = Date.now();
    const signal: AppResumeSignal = {
      id: ++sequence,
      at: now,
      awayMs: awayAt === null ? 0 : Math.max(0, now - awayAt),
      reasons: Array.from(pendingReasons).sort(),
    };
    away = false;
    awayAt = null;
    pendingReasons.clear();
    notify(signal);
  }, COALESCE_MS);
};

const onVisibility = () => {
  if (document.visibilityState === "hidden") {
    markAway();
    return;
  }
  requestResume("visible");
};

const onBlur = () => markAway();
const onFocus = () => requestResume("focus");

export function startAppResumeCoordinator(): void {
  if (started) return;
  started = true;
  away = document.visibilityState === "hidden";
  awayAt = away ? Date.now() : null;
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("blur", onBlur);
  window.addEventListener("focus", onFocus);
}

export function stopAppResumeCoordinator(): void {
  if (!started) return;
  document.removeEventListener("visibilitychange", onVisibility);
  window.removeEventListener("blur", onBlur);
  window.removeEventListener("focus", onFocus);
  if (timer) clearTimeout(timer);
  timer = null;
  pendingReasons.clear();
  away = false;
  awayAt = null;
  started = false;
}

export function subscribeAppResume(listener: ResumeListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function isAppForeground(): boolean {
  return document.visibilityState === "visible" && !away;
}

export const __testing = {
  reset() {
    stopAppResumeCoordinator();
    listeners.clear();
    sequence = 0;
  },
  coalesceMs: COALESCE_MS,
};
